/**
 * casesDocket — the pure derivations behind the Cases docket (A3), kept out of
 * the component so the row math is unit-tested without a render. Every figure
 * is off the wire. Reuses `parseUsd`/`parseDateOnly`/`formatClockDate` and
 * `PRO_LABEL`, so the docket never spells an amount, day or professional twice.
 */
import type {
  CaseEngagement,
  CaseSpine,
  CaseTicket,
  ViolationCase,
} from "@/queries/violationCasesQueries";
import { PRO_LABEL, isLive } from "@/components/portal/EngagementRowChip";
import {
  formatClockDate,
  nyDate,
  parseDateOnly,
  parseUsd,
} from "@/components/portal/violationRemediation";
import { overdueTag } from "./overdueTag";
import { CASE_RESOLVED } from "./caseCounts";
import { JUDGMENT_CLOCK_WORD, type JudgmentStep } from "./useActionPlanFeed";

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/** The rung the OWNER owes an answer on — the only owner-owed engagement state
 *  (the fan-out pauses here until the fee terms are approved). */
const OWNER_OWED_STATUS = "FEE_APPROVAL";

/** "$1.73" / "$3.70" — cents-precise, for the per-day costing. `formatUsdFull`
 *  rounds to whole dollars, which would print $3.70/day as "$4". */
export const formatUsdCents = (value: number): string =>
  value.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

/** Drop a trailing " (BF35)" the profile label carries — the pattern key is
 *  shown on its own beside the subject, so repeating it reads as a stutter. */
const stripPatternSuffix = (subject: string, patternKey: string): string => {
  const s = (subject || "").trim();
  const suffix = `(${(patternKey || "").trim()})`;
  return patternKey && s.endsWith(suffix)
    ? s.slice(0, -suffix.length).trim()
    : s;
};

/** Em/en dash → comma. Ruling: no em-dashes anywhere, INCLUDING a profile label
 *  off the wire ("... systems — unwarranted alarms" → "..., unwarranted alarms"). */
const commaForDash = (s: string): string => s.replace(/\s*[—–]\s*/g, ", ");

/** Docket row subject: the lead clause only (before the first em-dash), pattern
 *  suffix removed. A3: "FDNY fire protection systems". */
export const docketSubject = (
  subject: string | undefined,
  patternKey: string,
): string => {
  const head = stripPatternSuffix(subject ?? "", patternKey).split(
    /\s*[—–]\s*/,
  )[0];
  return (head || "").trim();
};

/** Open-case title: the full charge, dash swapped for a comma, suffix removed.
 *  A3: "FDNY fire protection systems, unwarranted alarms". */
export const openTitle = (
  subject: string | undefined,
  patternKey: string,
): string => commaForDash(stripPatternSuffix(subject ?? "", patternKey)).trim();

/** The open case card's heading and the rail row's, one formula (CC-150, 10-31). */
export const caseHeading = (c: {
  caseDisplaySubject?: string;
  patternKey: string;
  title?: string;
}): string =>
  openTitle(c.caseDisplaySubject, c.patternKey) ||
  c.title ||
  `Pattern ${c.patternKey}`;

/** Sum of the priced tickets' exposure + how many of the case's tickets are
 *  priced. "N of M" on the docket = priced of total (a terminal ticket resolves
 *  to no row and shows unpriced, reconciling to the hero). */
export interface AtStake {
  total: number;
  priced: number;
  ticketCount: number;
}
export const caseAtStake = (spine: CaseSpine | undefined): AtStake => {
  const tickets = spine?.tickets ?? [];
  let total = 0;
  let priced = 0;
  tickets.forEach((t) => {
    if (t.exposureUsd != null && t.exposureUsd !== "") {
      total += parseUsd(t.exposureUsd);
      priced += 1;
    }
  });
  return { total, priced, ticketCount: spine?.ticketCount ?? tickets.length };
};

/** Daily costing off the wire (`judgmentInterest.perDayUsd`); 0 when the key is
 *  absent (a stale persister payload shows the zero line, never a guess). */
export const casePerDay = (caseItem: ViolationCase): number =>
  parseUsd(caseItem.judgmentInterest?.perDayUsd);

/** Accrued interest to date off the wire (`judgmentInterest.totalUsd`). */
export const caseAccrued = (caseItem: ViolationCase): number =>
  parseUsd(caseItem.judgmentInterest?.totalUsd);

/** Non-completed engagements that still owe the OWNER an answer (fee approval). */
export const ownerOwedEngagements = (
  caseItem: ViolationCase,
): CaseEngagement[] =>
  (caseItem.engagements ?? []).filter((e) => e.status === OWNER_OWED_STATUS);

/** Does this case need the owner? (An engagement paused at fee approval.) */
export const caseNeedsYou = (caseItem: ViolationCase): boolean =>
  ownerOwedEngagements(caseItem).length > 0;

/** `released`: a CodeClear hand-off no live authorization covers since a revoke
 *  (violations_rep.released). It reads as the owner's, started or not. */
const held = (e: CaseEngagement): boolean => !e.released;

/** The docket's row tag. An open case with no live engagement is the owner's:
 *  Undo or a revoke hands it back and the case stays (CC-152, 10-09). */
export type CaseStanding = "needs-you" | "resolved" | "with-codeclear";
export const caseStanding = (caseItem: ViolationCase): CaseStanding => {
  if (caseNeedsYou(caseItem)) return "needs-you";
  if (caseItem.status === CASE_RESOLVED) return "resolved";
  return (caseItem.engagements ?? []).some((e) => isLive(e.status) && held(e))
    ? "with-codeclear"
    : "needs-you";
};

/** The most-live engagement per professional type, in a stable order — the ONE
 *  selector behind the docket subline, the callout AND the open case's chips, so
 *  a professional is never summarized two ways. Active outranks completed;
 *  within a rank the latest wins. */
const TYPE_ORDER = [
  "CODECLEAR",
  "LAWYER",
  "VENDOR",
  "EXPEDITER",
  "OWN_PROFESSIONAL",
];
export const liveEngagements = (caseItem: ViolationCase): CaseEngagement[] => {
  const best = new Map<string, CaseEngagement>();
  (caseItem.engagements ?? []).filter(held).forEach((e) => {
    const prev = best.get(e.professionalType);
    const rank = (x: CaseEngagement) => (isLive(x.status) ? 1 : 0);
    if (!prev || rank(e) >= rank(prev)) best.set(e.professionalType, e);
  });
  return TYPE_ORDER.filter((t) => best.has(t)).map((t) => best.get(t)!);
};

const STATUS_PHRASE: Record<string, string> = {
  REQUESTED: "requested",
  SOURCING: "being sourced",
  FEE_APPROVAL: "at fee approval",
  ENGAGED: "engaged",
  COMPLETED: "done",
};

const proWord = (type: string): string => {
  const word = PRO_LABEL[type as keyof typeof PRO_LABEL] ?? "Help";
  return type === "CODECLEAR" ? word : word.toLowerCase(); // a name keeps its case
};

/** The docket subline's engagement clause. One professional names its state
 *  ("attorney at fee approval"); several name themselves and the case ("attorney,
 *  vendor and your pro on it"). Empty when no one is engaged. */
export const engagementPhrase = (
  caseItem: ViolationCase,
  idleNote?: string,
): string => {
  const live = liveEngagements(caseItem);
  if (live.length === 0) return "";
  if (live.length === 1) {
    const state = STATUS_PHRASE[live[0].status] ?? "requested";
    const one = `${proWord(live[0].professionalType)} ${state}`;
    return idleNote ? `${one}, ${idleNote}` : one;
  }
  const words = live.map((e) => proWord(e.professionalType));
  const joined =
    words.length === 2
      ? `${words[0]} and ${words[1]}`
      : `${words.slice(0, -1).join(", ")} and ${words[words.length - 1]}`;
  // B2: idle work is never "on it"; the open case's chips carry the same note.
  return idleNote ? `${joined}, ${idleNote}` : `${joined} on it`;
};

/** Whole days by New York calendar date: a stored UTC-midnight deadline read at 9 AM
 *  counted "in 9 days" where the Action plan says 10 (CC-151, 10-09). */
const daysLate = (iso: string | null, today: Date): number | null => {
  const d = parseDateOnly(iso?.slice(0, 10));
  const t = parseDateOnly(nyDate(today));
  if (!d || !t) return null;
  return Math.round((t.getTime() - d.getTime()) / MS_PER_DAY);
};

const KIND_NOUN: Record<string, string> = {
  REOPEN: "reopen date",
  APPEAL: "appeal deadline",
};
const deadlineNoun = (kind: string): string => KIND_NOUN[kind] ?? "deadline";

/** oath_windows.leg_label: the legal date hangs on when OATH mailed the decision. */
export const MAYBE_CLOSED = "May have closed. Ask by";
export const spineMaybeClosed = (spine: CaseSpine | undefined): boolean =>
  !!spine?.earliestDeadlineMaybeClosed;

/** "Judgment owed" is the spine ticket's `judgmentOwedShown`, read as-is: 1D's
 *  case_clock.judgment_owed_shown writes it (28 on Related, 10-30), and the staff tile and the
 *  export count the same tickets. Never re-derive it here. Read only with `portal_judgment_owed` ON. */
export const JUDGMENT_OWED = "Judgment owed";
// The wire keys are not declared on CaseTicket yet: src/queries is outside this lane (reported).
type ClockTicket = CaseTicket & {
  judgmentOwedShown?: boolean;
  judgmentStep?: string | null;
  sortBy?: string | null;
};
/** The spine ticket keys judgment owed adds; the YC demo strips them (ycCaseStrip). A key added above fails tsc here. */
const CLOCK: Record<Exclude<keyof ClockTicket, keyof CaseTicket>, true> = {
  judgmentOwedShown: true,
  judgmentStep: true,
  sortBy: true,
};
export const CLOCK_KEYS: ReadonlySet<string> = new Set(Object.keys(CLOCK));
export const judgmentOwedShown = (t: CaseTicket): boolean =>
  (t as ClockTicket).judgmentOwedShown === true;

/** A judgment's Action plan step and date (server `case_spine._amnesty_fields`; the date only while ahead):
 *  "Amnesty price ends · Jan 8, 2027" on CC-150, "File certificate" on 18-12 past Mon Nov 9. Null with no step. */
export const judgmentClockText = (t: CaseTicket): string | null => {
  const { judgmentStep: step, sortBy } = t as ClockTicket;
  const word = step ? JUDGMENT_CLOCK_WORD[step as JudgmentStep] : undefined;
  const d = sortBy ? parseDateOnly(sortBy) : null;
  if (!word) return null;
  return d ? `${word} · ${formatClockDate(d)}` : word;
};

export const judgmentOwedTickets = (
  spine: CaseSpine | undefined,
): CaseTicket[] => (spine?.tickets ?? []).filter(judgmentOwedShown);

/** Owed with nothing filed or decided: the only tickets "no window left" is true of. */
const idleOwedTickets = (spine: CaseSpine | undefined): CaseTicket[] =>
  judgmentOwedTickets(spine).filter((t) => !t.filed && !t.decided);

/** No date left, a judgment owed and no filing awaiting a decision: a help request has no
 *  window to work in (B2, Leo 10-31; CC-150's 3 attorney requests). */
export const noWindowLeft = (spine: CaseSpine | undefined): boolean =>
  !spine?.earliestDeadlineAt &&
  idleOwedTickets(spine).length > 0 &&
  !(spine?.tickets ?? []).some((t) => t.filed && !t.decided);
export const NO_WINDOW_LEFT = "no window left to reopen or appeal";

/** The server names the stage (1D `label_owed_stage`, B2); the stepper, chat divider and ticket
 *  table read that one mark to drop the "0 of 3" count, the branch note and a second label. */
export const stageReadsJudgmentOwed = (
  stage: Pick<CaseSpine["stages"][number], "label">,
  judgmentOwed: boolean,
): boolean => judgmentOwed && stage.label === JUDGMENT_OWED;

/** The docket "Reopen deadline" column: how late (or how soon) the earliest
 *  un-actioned deadline is, plus its date and how many tickets carry one. */
export interface DeadlineReadout {
  /** "+2y late" / "in 12 days" / "due today" / "may have closed, ask by". */
  text: string;
  /** "Mar 17, 2024". */
  dateLabel: string;
  late: boolean;
  /** Number of tickets with an un-actioned deadline (drives "worst of N"). */
  count: number;
}
export const deadlineReadout = (
  spine: CaseSpine | undefined,
  today: Date = new Date(),
): DeadlineReadout | null => {
  const at = spine?.earliestDeadlineAt ?? null;
  if (!at) return null;
  const d = parseDateOnly(at);
  const days = daysLate(at, today);
  const count = (spine?.tickets ?? []).filter(
    (t) => t.nextDeadlineAt && !t.filed && !t.decided,
  ).length;
  let text: string;
  let late = false;
  if (spineMaybeClosed(spine) && (days == null || days <= 0)) {
    text = "may have closed, ask by";
  } else if (days == null || days === 0) {
    text = "due today";
  } else if (days > 0) {
    text = `${overdueTag(days)} late`;
    late = true;
  } else {
    const n = Math.abs(days);
    text = `in ${n} ${n === 1 ? "day" : "days"}`;
  }
  return { text, dateLabel: d ? formatClockDate(d) : at, late, count };
};

/** Ticket numbers past their un-actioned deadline (nothing filed, no decision). */
export const lapsedTickets = (
  spine: CaseSpine | undefined,
  today: Date = new Date(),
): string[] => {
  const iso = nyDate(today);
  return (spine?.tickets ?? [])
    .filter(
      (t) =>
        t.nextDeadlineAt &&
        !t.filed &&
        !t.decided &&
        t.nextDeadlineAt.slice(0, 10) < iso,
    )
    .map((t) => t.ticketNumber)
    .filter((n): n is string => !!n);
};

const NUMBER_WORD = [
  "Zero",
  "One",
  "Two",
  "Three",
  "Four",
  "Five",
  "Six",
  "Seven",
  "Eight",
  "Nine",
];
const numberWord = (n: number): string =>
  n >= 0 && n < NUMBER_WORD.length ? NUMBER_WORD[n] : String(n);

/**
 * The open case's plain "happening now" sentence. Names the lapsed tickets when
 * any deadline has passed; otherwise states the next date. No case-state jargon,
 * no rung numbers (ruling: plain sentences only).
 */
export const NO_DEADLINE_OPEN = "No deadline is open on this case.";
/** "A, B, C and 18 more": a case the size of 18-12 (21 tickets) never reads 21 numbers. */
const OWED_LISTED = 3;
const owedList = (tickets: readonly CaseTicket[]): string => {
  const nums = tickets
    .map((t) => t.ticketNumber)
    .filter(Boolean)
    .slice(0, OWED_LISTED);
  const more = tickets.length - nums.length;
  if (nums.length === 0) return "";
  return more > 0 ? `${nums.join(", ")} and ${more} more` : nums.join(", ");
};
export const happeningNow = (
  spine: CaseSpine | undefined,
  today: Date = new Date(),
  judgmentOwed = false,
): string => {
  const lapsed = lapsedTickets(spine, today);
  const noun = deadlineNoun(spine?.earliestDeadlineKind ?? "");
  const owed = judgmentOwed ? idleOwedTickets(spine) : [];
  const listed = owedList(owed);
  const owedLine =
    owed.length === 0
      ? ""
      : `${numberWord(owed.length)} ${owed.length === 1 ? "ticket has" : "tickets have"} ` +
        `a judgment owed${listed ? ` (${listed})` : ""}, with ${NO_WINDOW_LEFT}.`;
  const withOwed = (line: string): string =>
    owedLine ? `${line} ${owedLine}` : line;
  if (lapsed.length > 0) {
    const isOne = lapsed.length === 1;
    return withOwed(
      `${numberWord(lapsed.length)} ${isOne ? "ticket is" : "tickets are"} past ` +
        `${isOne ? "its" : "their"} ${noun} with nothing filed ` +
        `(${lapsed.join(", ")}). CodeClear records the filing or the decision.`,
    );
  }
  const read = deadlineReadout(spine, today);
  if (read && !read.late && spineMaybeClosed(spine)) {
    return withOwed(
      `The ${noun} may have closed, depending on when OATH mailed the decision. Ask by ${read.dateLabel}.`,
    );
  }
  if (read && !read.late) {
    return withOwed(
      `The next ${noun} is ${read.dateLabel} (${read.text}). CodeClear files before it closes.`,
    );
  }
  // B4 (Leo 10-31): the CTO's wording; OFF keeps HEAD's sentence.
  return judgmentOwed
    ? withOwed(NO_DEADLINE_OPEN)
    : "Nothing is past due on this case right now.";
};

// ---------------------------------------------------------------------------
// The docket sort. Ruling: default is costing descending; the sort names itself
// on screen. What needs the owner floats to the top under every key.
// ---------------------------------------------------------------------------

export type DocketSort = "costing" | "late" | "case";

export const SORT_LABEL: Record<DocketSort, string> = {
  costing: "what it costs per day",
  late: "how late it is",
  case: "case number",
};

const caseNumberOf = (caseItem: ViolationCase): number => {
  const m = /(\d+)/.exec(caseItem.caseReference ?? "");
  return m ? Number(m[1]) : 0;
};

/** Sort the docket: owner-owed cases first, then by the chosen key. With `portal_judgment_owed`
 *  ON, "how late it is" takes the Action plan list's groups (B5, Leo 10-31; design ruling B):
 *  dated, then judgments owed, then lapsed dates, then the rest. */
export const sortDocket = (
  cases: readonly ViolationCase[],
  sort: DocketSort,
  today: Date = new Date(),
  judgmentOwed = false,
): ViolationCase[] => {
  const keyed = cases.map((c) => {
    const days = daysLate(c.spine?.earliestDeadlineAt ?? null, today);
    const tier =
      days != null && days <= 0
        ? 0
        : judgmentOwedTickets(c.spine).length > 0
          ? 1
          : days != null
            ? 2
            : 3;
    return {
      c,
      needs: caseNeedsYou(c) ? 1 : 0,
      perDay: casePerDay(c),
      late: days ?? Number.NEGATIVE_INFINITY,
      tier: judgmentOwed ? tier : 0,
      num: caseNumberOf(c),
    };
  });
  keyed.sort((a, b) => {
    if (a.needs !== b.needs) return b.needs - a.needs;
    if (sort === "costing") return b.perDay - a.perDay;
    if (sort === "late")
      return a.tier !== b.tier ? a.tier - b.tier : b.late - a.late;
    return a.num - b.num;
  });
  return keyed.map((k) => k.c);
};

/** Total tickets across the docket ("N open · M tickets" in the header). */
export const totalTicketCount = (cases: readonly ViolationCase[]): number =>
  cases.reduce((sum, c) => sum + (c.spine?.ticketCount ?? 0), 0);
